import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * HERO — new variants (mountain-card · sunset-form · video-modal ·
 * water-fullbleed · portrait-grid · card-on-photo)
 * ════════════════════════════════════════════════════════════════════════ */
export function HeroExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const layout = block.layout;
  const buttons = c.buttons || [];

  if (layout === 'mountain-card') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative min-h-[640px] flex items-center justify-center p-6 overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-indigo-300 via-violet-400 to-rose-300" />}
        <div className="relative z-10 bg-white shadow-2xl rounded-2xl max-w-md w-full p-10 text-center" style={{ borderRadius: theme.radius || 16 }}>
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow || 'Sign Up Now'}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Easy to setup. Easy to maintain.'}</H1>
          {c.tagline && <p data-testid={tid('tagline')} className="mt-4 text-sm opacity-70" style={{ color: theme.ink }}>{c.tagline}</p>}
          <div className="mt-6 space-y-3">
            {(c.fields || ['Email','Password']).map((f, i) => (
              <input key={i} type={i===1?'password':'text'} placeholder={f} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-500" />
            ))}
          </div>
          {buttons.length > 0 ? (
            <div className="mt-6"><Btn block={block} onEdit={onEdit} text={buttons[0].text || 'Get Started'} link={buttons[0].link} primary buttonIndex={0} color={buttons[0].color} theme={theme} /></div>
          ) : <button className="mt-6 px-7 py-3 rounded-full text-sm font-bold tracking-wider uppercase text-white" style={{ background: theme.accent || '#2DD4BF' }}>Get Started</button>}
        </div>
      </section>
    );
  }

  if (layout === 'sunset-form') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative min-h-[560px] flex items-center justify-center px-6 py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #FB923C 0%, #F472B6 50%, #A78BFA 100%)' }}>
        {c.image_url && <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="relative z-10 max-w-xl text-center text-white">
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={{ ink: '#fff' }}  testid={tid('eyebrow')}>{c.eyebrow || 'Limited Time'}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-6xl"  testid={tid('title')}>{c.title || 'Sign up right now and get 30% off'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-6 text-lg opacity-90" placeholder='Tagline (optional)' />
          <div className="mt-8 flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <input type="email" placeholder="your@email.com" className="flex-1 px-4 py-3 rounded-full text-sm text-gray-900" />
            <button className="px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider text-white whitespace-nowrap" style={{ background: theme.accent || '#0E0E23' }}>{buttons[0]?.text || 'Sign Up'}</button>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'video-modal') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative min-h-[560px] flex items-center justify-center overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-pink-500 to-violet-600" />}
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 text-center text-white px-6 max-w-3xl">
          <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-6xl"  testid={tid('title')}>{c.title || 'Create a beautiful, responsive website in under 60 minutes'}</H1>
          <button className="mt-10 w-20 h-20 rounded-full bg-white/95 flex items-center justify-center hover:scale-110 transition-transform mx-auto shadow-2xl" aria-label="Play">
            <span className="ml-1 w-0 h-0 border-l-[18px] border-l-violet-600 border-y-[12px] border-y-transparent" />
          </button>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-6 text-sm opacity-90" placeholder='Tagline (optional)' />
        </div>
      </section>
    );
  }

  if (layout === 'water-fullbleed') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative min-h-[640px] flex items-end justify-start p-12 overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-b from-cyan-700 to-blue-900" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="relative z-10 max-w-2xl text-white">
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={{ ink: '#fff' }}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-5xl md:text-7xl"  testid={tid('title')}>{c.title || 'Powerful Generator'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-6 text-lg opacity-90 max-w-lg" placeholder='Tagline (optional)' />
          {buttons.length > 0 && <div className="mt-8 flex gap-3">{buttons.map((b,i) => <Btn block={block} onEdit={onEdit} key={i} text={b.text} link={b.link} primary={i===0} buttonIndex={i} color={b.color} theme={{ ...theme, ink: '#fff' }} />)}</div>}
        </div>
      </section>
    );
  }

  if (layout === 'portrait-grid') {
    const imgs = (c.gallery || c.images || []).slice(0, 3);
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="grid grid-cols-1 md:grid-cols-2 min-h-[560px]" style={{ background: theme.bg, color: theme.ink }}>
        <div className="px-8 md:px-16 py-16 md:py-24 flex flex-col justify-center">
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={theme}  testid={tid('title')}>{c.title || 'Online Market With The Biggest Product Choice'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-6 text-lg opacity-75" placeholder='Tagline (optional)' />
          {buttons.length > 0 && <div className="mt-10 flex gap-3">{buttons.map((b,i)=><Btn block={block} onEdit={onEdit} key={i} text={b.text} link={b.link} primary={i===0} buttonIndex={i} color={b.color} theme={theme} />)}</div>}
        </div>
        <div className="grid grid-cols-3 gap-2 p-2 bg-gray-100">
          {imgs.length === 0 ? Array.from({length:3}).map((_,i)=><div key={i} className="aspect-[2/3] bg-gradient-to-br from-gray-200 to-gray-400" />)
            : imgs.map((src,i)=><SafeImg key={i} src={src} alt="" className="w-full h-full object-cover aspect-[2/3]" />)}
        </div>
      </section>
    );
  }

  if (layout === 'card-on-photo') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative min-h-[640px] flex items-center p-12 overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 to-teal-900" />}
        <div className="relative z-10 bg-white shadow-2xl max-w-md p-10 ml-auto mr-4 md:mr-24" style={{ borderRadius: theme.radius || 8, color: theme.ink }}>
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Ready to use'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 text-sm opacity-75" placeholder='Tagline (optional)' />
          {buttons.length > 0 && <div className="mt-6"><Btn block={block} onEdit={onEdit} text={buttons[0].text} link={buttons[0].link} primary buttonIndex={0} color={buttons[0].color} theme={theme} /></div>}
        </div>
      </section>
    );
  }

  if (layout === 'dark-form-capture') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: '#0E0E23' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-white">
          <div>
            <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={{ ink: '#fff' }}  testid={tid('eyebrow')}>{c.eyebrow || 'Get Early Access'}</Eyebrow>
            <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-5xl"  testid={tid('title')}>{c.title || 'The Amazing Product'}</H1>
            <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-5 opacity-80 leading-relaxed max-w-md" placeholder='Tagline (optional)' />
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8" style={{ borderRadius: theme.radius || 16 }}>
            <p className="text-xs uppercase tracking-[0.18em] opacity-70 font-bold">Reserve your spot</p>
            <div className="mt-4 space-y-3">
              {(c.fields || ['Name','Email','Company']).map((f,i) => <input key={i} type={f.toLowerCase().includes('email')?'email':'text'} placeholder={f} className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-sm text-white placeholder:text-white/40" />)}
            </div>
            <button className="mt-5 w-full py-3 rounded-full text-sm font-bold uppercase tracking-wider text-white" style={{ background: theme.accent || '#7C3AED' }}>{buttons[0]?.text || 'Get Started'}</button>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'peacock-fullbleed') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative min-h-[640px] flex items-center justify-center overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-emerald-700 to-cyan-900" />}
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
        <div className="relative z-10 w-full max-w-6xl px-8 text-white">
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={{ ink: '#fff' }}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-5xl md:text-7xl"  testid={tid('title')}>{c.title || 'Beautiful by nature.'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-6 text-lg opacity-90 max-w-xl" placeholder='Tagline (optional)' />
          {buttons.length > 0 && <div className="mt-10 flex flex-wrap gap-3">{buttons.map((b,i) => <Btn block={block} onEdit={onEdit} key={i} text={b.text} link={b.link} primary={i===0} buttonIndex={i} color={b.color} theme={{ ...theme, ink: '#fff' }} />)}</div>}
        </div>
      </section>
    );
  }

  if (layout === 'illustration-hero') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <H1 block={block} onEdit={onEdit} field='title' theme={theme}  testid={tid('title')}>{c.title || 'Great things begin here.'}</H1>
            <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-6 text-lg opacity-75 max-w-md" placeholder='Tagline (optional)' />
            {buttons.length > 0 && <div className="mt-8 flex flex-wrap gap-3">{buttons.map((b,i) => <Btn block={block} onEdit={onEdit} key={i} text={b.text} link={b.link} primary={i===0} buttonIndex={i} color={b.color} theme={theme} />)}</div>}
          </div>
          <div className="aspect-square relative">
            {c.image_url ? <SafeImg src={c.image_url} alt="" className="w-full h-full object-contain" /> : (
              <div className="w-full h-full rounded-full bg-gradient-to-br from-violet-300 via-pink-300 to-amber-200 opacity-80" />
            )}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'login-split') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="grid grid-cols-1 md:grid-cols-2 min-h-[640px]">
        <div className="relative">
          {c.image_url ? <SafeImg src={c.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-amber-300 to-rose-400" />}
        </div>
        <div className="px-8 md:px-16 py-16 flex flex-col justify-center" style={{ background: theme.bg, color: theme.ink }}>
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow || 'Welcome back'}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Sign in to your account'}</H1>
          <div className="mt-8 space-y-3 max-w-sm">
            <input type="email" placeholder="Email" className="w-full px-4 py-3 border border-current/20 bg-transparent rounded-lg text-sm" />
            <input type="password" placeholder="Password" className="w-full px-4 py-3 border border-current/20 bg-transparent rounded-lg text-sm" />
          </div>
          <button className="mt-5 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider text-white w-fit" style={{ background: theme.accent || '#2DD4BF' }}>{buttons[0]?.text || 'Sign In'}</button>
        </div>
      </section>
    );
  }

  if (layout === 'laptop-footer-features') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto text-center">
          <H1 block={block} onEdit={onEdit} field='title' theme={theme}  testid={tid('title')}>{c.title || 'Built for your best work.'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-5 max-w-xl mx-auto opacity-75" placeholder='Tagline (optional)' />
          {buttons.length > 0 && <div className="mt-8"><Btn block={block} onEdit={onEdit} text={buttons[0].text} link={buttons[0].link} primary buttonIndex={0} color={buttons[0].color} theme={theme} /></div>}
          <div className="mt-12 aspect-video bg-gradient-to-br from-slate-800 to-black rounded-xl shadow-2xl overflow-hidden">
            {c.image_url && <SafeImg src={c.image_url} alt="" className="w-full h-full object-cover" />}
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {(c.mini_features || [
            { title: 'Fast', description: 'Lightning quick.' },
            { title: 'Secure', description: 'Bank-grade.' },
            { title: 'Simple', description: 'Just works.' },
          ]).slice(0,3).map((f,i) => (
            <div key={i}>
              <p className="font-bold text-sm">{f.title}</p>
              <p className="mt-1 text-xs opacity-70">{f.description}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }
  return null;
}
